import React, { useState } from 'react';

import { useApolloClient } from '@apollo/client';
import dayjs from 'dayjs';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { DAYS_OF_THE_WEEK, WEEKDAY_INFO, FORMAT } from 'constants/date-constants';

import { ChoosePunctualOrRecurringOrder } from './ChoosePunctualOrRecurring';
import { Loading } from 'components/Loading';
import { PreventReplacingOrderByItself } from './PreventReplacingOrderByItself';
import { ValidateReplacingOrder } from './ValidateReplacingOrder';

import { getRecurringOrdersForDay } from 'utils/orders';
import { totemCreatedCacheHandler } from 'utils/cache/totemCreatedCacheHandler';
import { orderDuplicatedCacheHandler } from 'utils/cache/orderDuplicatedCacheHandler';

import { analyticsEvents, analyticsPropertiesConstants, track } from 'tracking/segment';
import {
    useGetOrdersOfTotemQuery,
    useDuplicateOrderAndCreateTotemMutation,
    useDuplicateOrderMutation,
    useGetOrdersOfTotemWithinDateRangeQuery,
} from 'data/__generated__';

interface DuplicationLastStepProps {
    orderToDuplicate: {
        _id: string;
        [key: string]: any;
    };
    isCreatingNewTotem: boolean;
    destinationTotemId: string;
    currentTotemId: string;
    deliveryInfo: any;
    defaultCategoryId: string | undefined | null;
    selectedDate: moment.Moment | dayjs.Dayjs | null;
    selectedTimeWindow: any;
    handleCancel: () => void;
}

export const DuplicationLastStep: React.FC<DuplicationLastStepProps> = ({
    orderToDuplicate,
    isCreatingNewTotem,
    destinationTotemId,
    currentTotemId,
    deliveryInfo,
    defaultCategoryId,
    selectedDate,
    selectedTimeWindow,
    handleCancel,
}) => {
    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const [isConfirmingReplace, setIsConfirmingReplace] = useState(false);
    const startDate = selectedDate ? selectedDate.startOf('day').toDate().getTime() : undefined;
    const endDate = selectedDate ? selectedDate.endOf('day').toDate().getTime() : undefined;

    const shouldRunQuery = !!destinationTotemId && startDate !== undefined && endDate !== undefined;

    const {
        loading: ordersOfTotemLoading,
        error: ordersOfTotemError,
        data: ordersOfTotemData,
    } = useGetOrdersOfTotemQuery({ variables: { totemId: destinationTotemId }, skip: !destinationTotemId });

    const {
        loading: ordersOfTotemWithinDateRangeLoading,
        error: ordersOfTotemWithinDateRangeError,
        data: ordersOfTotemWithinDateRangeData,
    } = useGetOrdersOfTotemWithinDateRangeQuery({
        variables: {
            totemId: destinationTotemId,
            startDate: startDate as number,
            endDate: endDate as number,
            isPunctual: true,
        },
        skip: !shouldRunQuery,
        fetchPolicy: 'no-cache', // prevents from creating 2 consecutive orders for the same date
    });
    const [duplicateOrder, { loading: duplicateOrderIsLoading, error: duplicateOrderError }] =
        useDuplicateOrderMutation();
    const [
        duplicateOrderAndCreateTotem,
        { loading: duplicateOrderAndCreateTotemIsLoading, error: duplicateOrderAndCreateTotemOrderError },
    ] = useDuplicateOrderAndCreateTotemMutation();

    if (
        ordersOfTotemLoading ||
        ordersOfTotemWithinDateRangeLoading ||
        duplicateOrderIsLoading ||
        duplicateOrderAndCreateTotemIsLoading
    ) {
        return <Loading />;
    }
    if (
        ordersOfTotemError ||
        ordersOfTotemWithinDateRangeError ||
        duplicateOrderError ||
        duplicateOrderAndCreateTotemOrderError
    ) {
        const errorMessage =
            ordersOfTotemError?.message ||
            ordersOfTotemWithinDateRangeError?.message ||
            duplicateOrderError?.message ||
            duplicateOrderAndCreateTotemOrderError?.message ||
            'Unknown error';
        throw new Error(errorMessage);
    }

    const hasSameDayPunctualOrder = ordersOfTotemWithinDateRangeData?.ordersOfTotemWithinDateRange;
    let weekdayIndex: number | undefined = undefined;
    if (selectedDate) {
        if (typeof (selectedDate as any).day === 'function') {
            weekdayIndex = (selectedDate as any).day();
        }
    }
    const weekday = typeof weekdayIndex === 'number' ? DAYS_OF_THE_WEEK[weekdayIndex] : undefined;
    const translatedWeekday = WEEKDAY_INFO.find((day) => day.value === weekday)?.name ?? '';
    const { modifiableOrder: sameDayModifiableRecurringOrder } = getRecurringOrdersForDay(
        ordersOfTotemData?.ordersOfTotem ?? null,
        weekday,
    );

    const handleDuplicateOrder = async (isOrderPunctual: boolean, sameDayRecurringOrder: any) => {
        if (!selectedDate) {
            return;
        }
        if (isCreatingNewTotem) {
            const result = await duplicateOrderAndCreateTotem({
                variables: {
                    orderToDuplicateId: orderToDuplicate._id,
                    deliveryInfo: deliveryInfo,
                    deliveryDate: selectedDate?.format(FORMAT),
                    deliveryTimeWindow: selectedTimeWindow,
                    isOrderPunctual,
                },
            });
            const duplicateOrderAndCreateTotemData = result.data?.duplicateOrderAndCreateTotem;
            if (!duplicateOrderAndCreateTotemData) {
                return;
            }
            const { order: newOrder, totem: newTotem, user: updatedUser } = duplicateOrderAndCreateTotemData;
            track(analyticsEvents.DUPLICATE_ORDER, {
                [analyticsPropertiesConstants.ORDER.DUPLICATION.IS_FOR_SAME_TOTEM]:
                    destinationTotemId === currentTotemId,
                [analyticsPropertiesConstants.ORDER.DUPLICATION.IS_CREATING_NEW_TOTEM]: true,
                [analyticsPropertiesConstants.ORDER.IS_PUNCTUAL]: isOrderPunctual,
                [analyticsPropertiesConstants.ORDER.DUPLICATION.IS_REPLACING]: false,
            });
            totemCreatedCacheHandler({ apolloClient, newTotem, updatedUser });
            navigate(`/totem/${newTotem?._id}/${defaultCategoryId}/${newOrder?._id}`, { relative: 'path' });
        } else {
            let deliveryDate = selectedDate?.format(FORMAT);
            if (weekday === sameDayRecurringOrder?.weekday) {
                // replace next weekend recurring order
                deliveryDate = sameDayRecurringOrder.date_delivery;
            }
            const result = await duplicateOrder({
                variables: {
                    orderToDuplicateId: orderToDuplicate._id,
                    destinationTotemId,
                    deliveryDate: deliveryDate,
                    deliveryTimeWindow: selectedTimeWindow,
                    isOrderPunctual,
                },
            });
            const newOrder = result.data?.duplicateOrder;
            track(analyticsEvents.DUPLICATE_ORDER, {
                [analyticsPropertiesConstants.ORDER.DUPLICATION.IS_FOR_SAME_TOTEM]:
                    destinationTotemId === currentTotemId,
                [analyticsPropertiesConstants.ORDER.DUPLICATION.IS_CREATING_NEW_TOTEM]: false,
                [analyticsPropertiesConstants.ORDER.IS_PUNCTUAL]: isOrderPunctual,
                [analyticsPropertiesConstants.ORDER.DUPLICATION.IS_REPLACING]: isConfirmingReplace,
            });
            orderDuplicatedCacheHandler({
                apolloClient,
                destinationTotemId,
                orderBeingReplaced: sameDayRecurringOrder,
                ordersOfDestinationTotem: ordersOfTotemData?.ordersOfTotem,
                newOrder,
            });
            navigate(`/totem/${destinationTotemId}/${defaultCategoryId}/${newOrder?._id}`, { relative: 'path' });
        }
    };

    return (
        <Container>
            {isConfirmingReplace ? (
                sameDayModifiableRecurringOrder?._id === orderToDuplicate._id ? (
                    // trying to replace an order by itself
                    <PreventReplacingOrderByItself
                        defaultCategoryId={defaultCategoryId}
                        destinationTotemId={destinationTotemId}
                        handleCancel={handleCancel}
                        sameDayModifiableRecurringOrder={sameDayModifiableRecurringOrder}
                    />
                ) : (
                    // trying to replace an already exising recurring order
                    <ValidateReplacingOrder
                        defaultCategoryId={defaultCategoryId}
                        destinationTotemId={destinationTotemId}
                        handleCancel={handleCancel}
                        handleDuplicateOrder={handleDuplicateOrder}
                        sameDayModifiableRecurringOrder={sameDayModifiableRecurringOrder}
                        translatedWeekday={translatedWeekday}
                    />
                )
            ) : (
                <ChoosePunctualOrRecurringOrder
                    handleDuplicateOrder={handleDuplicateOrder}
                    hasSameDayPunctualOrder={hasSameDayPunctualOrder}
                    sameDayModifiableRecurringOrder={sameDayModifiableRecurringOrder}
                    setIsConfirmingReplace={setIsConfirmingReplace}
                    translatedWeekday={translatedWeekday}
                />
            )}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 20px;
`;
